/**
 * useVehiclesDashboardOverview - Indicadores da aba Geral do dashboard de Veículos
 *
 * Consome a RPC `get_vehicles_dashboard_overview` (SECURITY DEFINER), que devolve
 * em um único JSONB todos os blocos da aba: consolidado da frota, KPIs com
 * comparação ao período anterior, movimentação mensal, status, localização,
 * idade, marcas, modelos e Top 10 clientes. Nenhuma leitura direta de tabela no frontend.
 *
 * @version 1.2.0
 * RELEASE: 2026-10-03 v1.2.0 - Inclui status e localização geográfica na
 *          mesma resposta agregada, sem criar uma segunda busca.
 * RELEASE: 2026-10-03 v1.1.1 - Corrige a chamada RPC sem vínculo ao cliente
 *          (perdia o `this`, falhava antes da rede e o painel ficava zerado).
 * RELEASE: 2026-10-03 v1.1.0 - Inclui o caminho seguro da logo de cada marca.
 * RELEASE: 2026-10-03 v1.0.0 - Criação inicial
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { VehicleType } from '@/hooks/useCustomerVehicles';

const logger = createLogger('useVehiclesDashboardOverview');

/** Filtros do cabeçalho do dashboard de Veículos */
export interface VehiclesDashboardFilters {
  startDate: Date;
  endDate: Date;
  vehicleType: VehicleType | null;
  categoryId: string | null;
  statusId: string | null;
}

export interface VehiclesLabelValue {
  label: string;
  value: number;
}

export interface VehiclesBrandValue extends VehiclesLabelValue {
  logo_path: string | null;
}

export interface VehiclesStatusValue extends VehiclesLabelValue {
  id: string;
  color: string;
}

export interface VehiclesLocationValue extends VehiclesLabelValue {
  id: string;
  state?: string | null;
}

export interface VehiclesMonthlyPoint {
  month: string;
  registrations: number;
  transfers: number;
  inactivations: number;
  cumulative: number;
}

export interface VehiclesTopCustomer {
  rank: number;
  customer_id: string;
  customer_name: string;
  vehicles: number;
  fipe_total: number;
}

export interface VehiclesDashboardOverview {
  consolidated: {
    total: number;
    fipe_total: number;
    total_at_start: number;
    by_type: Record<VehicleType, number>;
  };
  kpis: {
    new_current: number;
    new_previous: number;
    transfers_current: number;
    transfers_previous: number;
    fipe_avg: number;
    fipe_avg_previous: number;
    cancelled_total: number;
    cancelled_current: number;
    cancelled_previous: number;
  };
  monthly: VehiclesMonthlyPoint[];
  by_age: Array<{ bucket: string; value: number }>;
  by_brand: VehiclesBrandValue[];
  by_model: VehiclesLabelValue[];
  top_customers: VehiclesTopCustomer[];
  by_status: VehiclesStatusValue[];
  by_location: {
    regions: VehiclesLocationValue[];
    states: VehiclesLocationValue[];
    cities: VehiclesLocationValue[];
  };
}

/** Chave base do cache (usada também pela invalidação via Realtime) */
export const VEHICLES_DASHBOARD_QUERY_KEY = 'vehicles-dashboard-overview';

const n = (v: unknown) => Number(v) || 0;
type JsonObject = Record<string, unknown>;

/** Converte valores JSON desconhecidos em objetos seguros para leitura. */
const objectValue = (value: unknown): JsonObject => (
  value && typeof value === 'object' && !Array.isArray(value) ? value as JsonObject : {}
);

/** Converte valores JSON desconhecidos em listas seguras para leitura. */
const arrayValue = (value: unknown): unknown[] => Array.isArray(value) ? value : [];

export function useVehiclesDashboardOverview(filters?: VehiclesDashboardFilters) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [
      VEHICLES_DASHBOARD_QUERY_KEY,
      tenantId,
      filters?.startDate.toISOString(),
      filters?.endDate.toISOString(),
      filters?.vehicleType,
      filters?.categoryId,
      filters?.statusId,
    ],
    enabled: !!tenantId && !!filters,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
    queryFn: async (): Promise<VehiclesDashboardOverview> => {
      if (!filters || !tenantId) throw new Error('Filtros do dashboard de veículos indisponíveis');
      // v1.1.1: `bind` preserva o `this` do cliente; sem ele a requisição nunca é enviada.
      const rpc = supabase.rpc.bind(supabase) as unknown as (
        name: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: { message: string } | null }>;
      const { data, error } = await rpc('get_vehicles_dashboard_overview', {
        p_start_date: filters.startDate.toISOString(),
        p_end_date: filters.endDate.toISOString(),
        p_vehicle_type: filters.vehicleType,
        p_category_id: filters.categoryId,
        p_status_id: filters.statusId,
        p_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo',
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao carregar dashboard de veículos', { error: error.message });
        throw error;
      }
      const raw = objectValue(data);
      const c = objectValue(raw.consolidated);
      const k = objectValue(raw.kpis);
      const byType = objectValue(c.by_type);
      const byLocation = objectValue(raw.by_location);
      const lv = (value: unknown) => arrayValue(value).map((item) => {
        const row = objectValue(item);
        return { label: String(row.label ?? '—'), value: n(row.value) };
      });
      return {
        consolidated: {
          total: n(c.total),
          fipe_total: n(c.fipe_total),
          total_at_start: n(c.total_at_start),
          by_type: {
            CAR: n(byType.CAR),
            MOTORCYCLE: n(byType.MOTORCYCLE),
            TRUCK: n(byType.TRUCK),
            OTHER: n(byType.OTHER),
          },
        },
        kpis: {
          new_current: n(k.new_current),
          new_previous: n(k.new_previous),
          transfers_current: n(k.transfers_current),
          transfers_previous: n(k.transfers_previous),
          fipe_avg: n(k.fipe_avg),
          fipe_avg_previous: n(k.fipe_avg_previous),
          cancelled_total: n(k.cancelled_total),
          cancelled_current: n(k.cancelled_current),
          cancelled_previous: n(k.cancelled_previous),
        },
        monthly: arrayValue(raw.monthly).map((item) => {
          const m = objectValue(item);
          return {
            month: String(m.month),
            registrations: n(m.registrations),
            transfers: n(m.transfers),
            inactivations: n(m.inactivations),
            cumulative: n(m.cumulative),
          };
        }),
        by_age: arrayValue(raw.by_age).map((item) => {
          const age = objectValue(item);
          return { bucket: String(age.bucket), value: n(age.value) };
        }),
        by_brand: arrayValue(raw.by_brand).map((item) => {
          const brand = objectValue(item);
          return {
            label: String(brand.label ?? '—'),
            value: n(brand.value),
            logo_path: typeof brand.logo_path === 'string' ? brand.logo_path : null,
          };
        }),
        by_model: lv(raw.by_model),
        top_customers: arrayValue(raw.top_customers).map((item) => {
          const customer = objectValue(item);
          return {
            rank: n(customer.rank),
            customer_id: String(customer.customer_id),
            customer_name: String(customer.customer_name ?? '—'),
            vehicles: n(customer.vehicles),
            fipe_total: n(customer.fipe_total),
          };
        }),
        by_status: arrayValue(raw.by_status).map((item) => {
          const status = objectValue(item);
          return {
            id: String(status.id ?? '__sem_status'),
            label: String(status.label ?? 'Sem status'),
            color: String(status.color ?? 'hsl(var(--muted-foreground))'),
            value: n(status.value),
          };
        }),
        by_location: {
          regions: arrayValue(byLocation.regions).map((item) => {
            const location = objectValue(item);
            return { id: String(location.id ?? ''), label: String(location.label ?? 'Não informada'), value: n(location.value) };
          }),
          states: arrayValue(byLocation.states).map((item) => {
            const location = objectValue(item);
            return { id: String(location.id ?? ''), label: String(location.label ?? 'Não informada'), value: n(location.value) };
          }),
          cities: arrayValue(byLocation.cities).map((item) => {
            const location = objectValue(item);
            return {
              id: String(location.id ?? ''),
              label: String(location.label ?? 'Não informada'),
              state: typeof location.state === 'string' ? location.state : null,
              value: n(location.value),
            };
          }),
        },
      };
    },
  });
}
